import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ButtonDesigned from '../../Buttons/ButtonDesigned';
import { ViewDocumentOverlay } from '../../ViewDocumentOverlay';
import { AttachFileErrorScreen } from '../ErrorComponents/uploadFileErrorScreen';
import {
  DEFAULT_APPROVED_FILE_FORMATS,
  DEFAULT_MAX_FILE_SIZE,
  fetchDocumentsFromStorage,
  getDropzoneOrderConfig,
  handleRemoveFile,
  IUploadErrorObject,
} from '../fileUploadUtils';
import { AttachFileAction } from './components/ActionButton';
import { MultiFileUploadWizard } from './components/MultiFileUploadWizard';
import { comment } from '../../../../sp-types/FutureOrderType';

interface Props {
  handleSubmitWithFilePath?: (_paths: string[]) => void; // Put state to pending if needed
  fileNameSuffix?: string;
  handleCancelSubmit?: () => void; // Change State to underWriting if needed
  handleActionButton?: () => void;
  bucketSaveLocation?: string; // Change to needed Storage place
  dropZoneTitle?: [string, string, string];
  actionButtonTitle?: string;
  dropZoneText?: string;
  dropZoneButtonText?: [string, string, string];
  actionButtonText?: string;
  dropZoneImage?: string;
  actionButtonImage?: string;
  onLoadChange?: (_isLoading: boolean) => void;
  bottomText?: string;
  maxFileSizeInMb?: number;
  fileFormatAllowed?: string[];
  onFilesChanged: (_files: string[]) => void;
  onCommentsChanged: (_files: comment[]) => void;
  comments: comment[];
  uploadedFiles: string[];
}

interface IFileAttachmentStageProps {
  title: string;
  titleColor: 'purple' | 'gray-400';
  bgColor: 'white' | 'gray-400/5';
  subTitle: string;
  button: { text: string; isBlue: boolean };
  borderColor: 'blue-600' | 'gray-700/40';
  actionButton: () => void;
}

export const AttachFileActionButton: React.FC<Props> = ({
  handleActionButton = () => {},
  dropZoneImage = '/assets/icons/red/attach-file.svg',
  dropZoneTitle = ['Drag and Drop file', 'File attached', 'Pending...'],
  dropZoneText = 'click the Browse button below',
  dropZoneButtonText = ['Browse', 'Submit', 'Cancel'],
  actionButtonImage = '/assets/icons/red/create-order.svg',
  actionButtonTitle = 'Create an order',
  actionButtonText = 'Click to create a new order',
  bucketSaveLocation = 'TempFiles/',
  handleCancelSubmit,
  handleSubmitWithFilePath,
  fileNameSuffix = '',
  onLoadChange,
  onFilesChanged,
  uploadedFiles,
  comments,
  onCommentsChanged,
  bottomText = 'The file should contain order details, such as product materials quantities and required prices You can add multiple file formats like Word, PDF, image, or Excel',
  maxFileSizeInMb = DEFAULT_MAX_FILE_SIZE,
  fileFormatAllowed = DEFAULT_APPROVED_FILE_FORMATS,
}) => {
  const [importedFilePaths, setImportedFilePaths] = useState<string[]>(uploadedFiles || []);
  const [showOverlay, setShowOverlay] = useState<{ show: boolean; index?: number }>({ show: false });
  const [fileUrls, setFileUrls] = useState<string[]>([]);
  const [fileAttachmentStage, setFileAttachmentStage] = useState<'empty' | 'uploadedFile' | 'pending'>('empty');
  const [isUploadError, setIsUploadError] = useState<IUploadErrorObject>({
    value: false,
    errorType: 'none',
  });

  useEffect(() => {
    if (importedFilePaths.length > 0) {
      fetchDocumentsFromStorage(importedFilePaths, (value: string[]) => setFileUrls(value));
      setFileAttachmentStage('uploadedFile');
    }
    onFilesChanged(importedFilePaths);
  }, [importedFilePaths]);

  const statusInfo: Record<'empty' | 'uploadedFile' | 'pending', IFileAttachmentStageProps> = {
    empty: {
      title: dropZoneTitle[0],
      titleColor: 'purple',
      bgColor: 'white',
      subTitle: dropZoneText,
      button: { text: dropZoneButtonText[0], isBlue: false },
      borderColor: 'gray-700/40',
      actionButton: () => {},
    },
    uploadedFile: {
      title: dropZoneTitle[1],
      titleColor: 'purple',
      bgColor: 'white',
      subTitle: dropZoneText,
      button: { text: dropZoneButtonText[1], isBlue: true },
      borderColor: 'blue-600',
      actionButton: () => {
        handleSubmitWithFilePath && handleSubmitWithFilePath(importedFilePaths);
        setFileAttachmentStage('pending');
      },
    },
    pending: {
      title: dropZoneTitle[2],
      titleColor: 'gray-400',
      bgColor: 'gray-400/5',
      subTitle: dropZoneText,
      button: { text: dropZoneButtonText[2], isBlue: false },
      borderColor: 'gray-700/40',
      actionButton: () => {
        handleCancelSubmit && handleCancelSubmit();
        setFileAttachmentStage('uploadedFile');
      },
    },
  };

  const onDeletePath = async (path: string): Promise<void> => {
    onLoadChange?.(true);
    await handleRemoveFile(path, importedFilePaths, setFileAttachmentStage, setImportedFilePaths);
    onLoadChange?.(false);
  };

  const dropzoneConfig = getDropzoneOrderConfig(
    importedFilePaths,
    setImportedFilePaths,
    setIsUploadError,
    bucketSaveLocation,
    fileNameSuffix,
    maxFileSizeInMb,
    fileFormatAllowed,
    true,
    onLoadChange,
  );

  const { getRootProps: getRootProps1, getInputProps: getInputProps1 } = useDropzone(dropzoneConfig);

  return (
    <div className="flex flex-col flex-1  h-full py-8 justify-evenly  gap-12 items-center ">
      {fileAttachmentStage === 'empty' ? (
        <div className="flex flex-row   gap-12  ">
          <div
            {...getRootProps1({
              className: `flex flex-col w-[15rem]  h-[17rem] py-6 gap-6 overflow-hidden  border-dashed border-1 rounded bg-${statusInfo[fileAttachmentStage].bgColor} border-${statusInfo[fileAttachmentStage].borderColor} pointer-events-auto`,
            })}
          >
            <input {...getInputProps1({ className: ' ' })} />
            <img className="self-center aspect-auto w-[3.625rem]" src={dropZoneImage} alt=""></img>
            <div className="flex flex-col gap-3 overflow-auto  ">
              <p className={` text-center text-xl text-${statusInfo[fileAttachmentStage].titleColor}`}>
                {statusInfo[fileAttachmentStage].title}
              </p>
              <div>
                <p className="text-center text-sm text-secondary_text">or</p>
                <p className="text-center text-sm text-secondary_text">{dropZoneText}</p>
              </div>
            </div>
            <div className="flex justify-center mt-auto">
              <ButtonDesigned
                onButtonClick={() => statusInfo[fileAttachmentStage].actionButton()}
                style={statusInfo[fileAttachmentStage].button.isBlue ? 'primary' : 'outline'}
                className="mt-0"
              >
                {statusInfo[fileAttachmentStage].button.text}
              </ButtonDesigned>
            </div>
          </div>
          <AttachFileAction
            actionButtonImage={actionButtonImage}
            actionButtonText={actionButtonText}
            actionButtonTitle={actionButtonTitle}
            handleActionButton={handleActionButton}
            isLoading={false}
          />

          {showOverlay.show && (
            <ViewDocumentOverlay
              fileUrl={fileUrls}
              uploadedDocumentPath={importedFilePaths}
              setShowOverlay={() => setShowOverlay({ show: false, index: 0 })}
              startingIndex={showOverlay.index || 0}
            />
          )}
        </div>
      ) : (
        <MultiFileUploadWizard
          fileUrls={fileUrls}
          comments={comments}
          onCommentsChanged={onCommentsChanged}
          filePaths={importedFilePaths}
          dropzoneConfig={dropzoneConfig}
          onDeleteFile={onDeletePath}
          gotoManualInput={handleActionButton}
        />
      )}
      <div className="flex flex-row   gap-2 text-sm max-w-[40rem]">
        <img className="h-5 w-5" src="/assets/icons/red/note.svg" />
        {bottomText}
      </div>
      {isUploadError.value && (
        <AttachFileErrorScreen
          errorType={isUploadError.errorType}
          setDisplayOverlay={(value: boolean) => setIsUploadError({ errorType: 'none', value: value })}
        />
      )}
    </div>
  );
};
